/**
 * CardTip — a small ⓘ icon you drop in the top-right of any card.
 * On hover (or keyboard focus) it reveals a compact tooltip that lists
 * what the card does and the shortcuts/actions it supports. Use it to
 * help agents orient themselves without launching the full guided tour.
 *
 * Usage:
 *   <CardTip
 *     title="Campaign queue"
 *     items={["Grouped by campaign", "Expand to see leads", "Click Call to start dial"]}
 *   />
 */
import React, { useState } from "react";
import { Info } from "lucide-react";

export default function CardTip({ title, items = [], testId, iconSize = 14, align = "right", as = "button" }) {
  const [open, setOpen] = useState(false);
  const show = () => setOpen(true);
  const hide = () => setOpen(false);
  const styles = {
    wrap: { position: "relative", display: "inline-flex", alignItems: "center" },
    btn: {
      width: iconSize + 6, height: iconSize + 6, borderRadius: "50%",
      border: "none", background: "rgba(37,99,235,0.08)", color: "#2563eb",
      cursor: "help", display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: 0, fontFamily: "var(--font)",
    },
    pop: {
      position: "absolute",
      top: iconSize + 10,
      [align]: 0,
      width: 260,
      background: "#0F2942",
      color: "#fff",
      borderRadius: 8,
      padding: "10px 12px",
      boxShadow: "0 10px 24px rgba(0,0,0,0.28)",
      fontSize: 11,
      lineHeight: 1.5,
      zIndex: 40,
      pointerEvents: "none",  // decorative — hover-only
    },
    title: { fontSize: 11, fontWeight: 700, marginBottom: 6, color: "#93C5FD", letterSpacing: "0.2px", textTransform: "uppercase" },
    ul: { margin: 0, paddingLeft: 16 },
    li: { marginBottom: 3, color: "#CBD5E0" },
  };
  // Use a <span> instead of a <button> when the CardTip is rendered inside
  // another interactive element (e.g. a toggle button). Nested <button>s are
  // invalid HTML and React 19 throws a hydration warning.
  const Trigger = as === "span" ? "span" : "button";
  const triggerProps = as === "span"
    ? { role: "img", "aria-label": `Tips for ${title || "this card"}`, style: { ...styles.btn, cursor: "help" }, onClick: (e) => e.stopPropagation() }
    : { "aria-label": `Tips for ${title || "this card"}`, style: styles.btn, tabIndex: 0, type: "button", onClick: (e) => e.stopPropagation() };
  return (
    <span style={styles.wrap} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      <Trigger data-testid={testId} {...triggerProps}>
        <Info style={{ width: iconSize, height: iconSize }} />
      </Trigger>
      {open && (
        <div role="tooltip" data-testid={testId ? `${testId}-tooltip` : undefined} style={styles.pop}>
          {title && <div style={styles.title}>{title}</div>}
          {items.length > 0 ? (
            <ul style={styles.ul}>
              {items.map((t, i) => (<li key={i} style={styles.li}>{t}</li>))}
            </ul>
          ) : null}
        </div>
      )}
    </span>
  );
}
